/**
 * C2 · THE funnel copy file.
 *
 * Every customer-facing string in the consumer funnel lives here, keyed by
 * screen. This is the ONE place to edit wording in code.
 *
 * How a change flows:
 *   1. Edit the string here → it is the code DEFAULT for that key.
 *   2. Each screen's key list (src/content/screens/*.ts) points its `default`
 *      at this map, so nothing is duplicated and no key can drift.
 *   3. Screens read keys through `useSiteContent(SECTION, DEFAULTS)`, which
 *      resolves: stored override (edited at /platform/website) → this default.
 *      A stored override always wins, so a live copy change needs no deploy;
 *      editing this file changes what ships when there is no override.
 *
 * Placeholders in braces (e.g. {styleName}, {{threshold}}) are substituted by the
 * screen that owns them — keep them intact.
 *
 * Do NOT add a string here that asserts anything about retention, consent, money
 * or timing unless it was specified; those claims are reviewed copy.
 */
export const FUNNEL_COPY: Record<string, string> = {
  /* ── S1 · Style gallery (/create/style) ── */
  "s1.header.backLabelRepeat": "Back to your account",
  "s1.header.backLabelNew": "Back to home",
  "s1.header.stepRepeat": "Step 1 of 3",
  "s1.header.stepNew": "Step 1 of 4",
  "s1.header.titleRepeat": "Pick your next one.",
  "s1.header.titleNew": "Pick the you you want on it.",
  "s1.header.subtitleRepeat":
    "Your face is already done. Pick a style you don't have yet — the ones you already hold are marked.",
  "s1.header.subtitleNew": "Choose your style.",
  "s1.filters.gridLabel": "Grid view",
  "s1.filters.swipeLabel": "Swipe view",
  "s1.body.loading": "Loading…",
  "s1.body.ownedBadge": "You have this",
  "s1.actions.countPrompt": "Tap a style to start",
  "s1.actions.countSelected": "1 style selected",
  "s1.actions.countOwned": "You already have this one — a second is fine",
  "s1.actions.continueReuse": "Use my face →",
  "s1.actions.continueNew": "Add my face →",
  "s1.errors.loadFailed": "Could not load styles.",

  /* ── S1b · Age affirmation (shown on /create/verify) ── */
  "s1b.affirm.hint": "We don't ask for your date of birth or store your age — we record only that you confirmed.",
  /* {minAge} resolves from the PROGRAMME age gate (`effective_age_gate`). Never type the number:
     a typed threshold would silently disagree with the gate counsel configured. */
  "s1b.refusal.line1": "Sorry — you need to be {minAge} or over to make a Cardmoji.",
  "s1b.refusal.line2": "Come back when you are, and we'll be here.",
  "s1b.refusal.home": "Back to home",

  /* ── S2 · Selfie (/create/selfie) ── */
  "s2.header.backLabel": "Back to styles",
  "s2.header.step": "Step 2 of 4",
  "s2.style.label": "Your style",
  "s2.style.fallbackName": "Your style",
  "s2.style.change": "Change",
  "s2.header.title": "Now the only bit we need from you.",
  "s2.header.subtitle": "One clear photo of your face. Thirty seconds, and you'll see yourself on the card.",
  "s2.dropzone.hint": "Line your face up\ninside the guide",
  "s2.good.title": "What works",
  "s2.good.light": "Good light on your face. Turn towards a window, or step outside.",
  "s2.good.alone": "Just you. One face in the frame — no friends, no pets in this one.",
  "s2.good.straight": "Look straight at the lens. Head up, shoulders in, phone at eye level.",
  "s2.good.smile": "Smile, or don't. Both work — a smile just tends to look more like you.",
  "s2.bad.title": "What we'll reject",
  "s2.bad.intro": "Cards carry your name and get printed, so these get sent back:",
  "s2.bad.sunglasses": "Sunglasses, masks or hats over the eyes. We need to see your face to draw it.",
  "s2.bad.filters": "Heavy filters or beauty smoothing. The avatar comes out looking like the filter, not you.",
  "s2.bad.otherFace": "Someone else's face. Celebrities, characters and photos of other people all get declined.",
  "s2.bad.logos": "Logos, slogans or offensive content. Anything a card scheme won't print, we can't either.",
  "s2.bad.blurry": "Dark, blurry or far away. If we can't see your features, neither can the generator.",
  "s2.privacy.body":
    "Your photo is used to make your avatar and nothing else. It isn't posted anywhere, and you can delete it any time from your account.",
  "s2.privacy.link": "How we handle your photo",
  "s2.reconsent.body":
    "This is a new photo, so we need your consent again: I consent to this selfie being processed to generate an avatar (biometric processing). It replaces the photo currently on file.",
  "s2.body.uploading": "Uploading…",
  "s2.body.readyRepeat": "Ready. Not yet linked — tick the consent above to continue.",
  "s2.body.readyNew": "Ready. Not yet linked — consent captured on the next step.",
  "s2.actions.capture": "Smile for a selfie",
  "s2.actions.upload": "Upload a photo",
  "s2.actions.uploadAlt": "Upload a photo instead",
  "s2.actions.captureAlt": "Smile for a selfie instead",
  "s2.actions.retake": "Retake or choose another",
  "s2.actions.continue": "Continue",
  "s2.actions.recordingConsent": "Recording consent…",
  "s2.errors.notSignedIn": "Not signed in.",
  "s2.errors.consentFailed": "Could not record your consent.",
  "s2.errors.uploadUrlFailed": "Could not get upload URL",
  "s2.errors.uploadFailed": "Upload failed",

  /* ── S2b · Same face (/create/same-face) ── */
  "s2b.header.backLabel": "Back to styles",
  "s2b.header.step": "Step 2 of 3",
  "s2b.style.label": "Your style",
  "s2b.style.fallbackName": "Your style",
  "s2b.header.title": "Same face as last time?",
  "s2b.header.subtitle":
    "Your photo is already on file, so this one can skip the camera. Take a new photo instead if you'd rather start fresh.",
  "s2b.reuse.title": "Use the photo I already have",
  "s2b.reuse.body":
    "No new photo needed — we draw a fresh avatar from the one already on file. Straight to generating.",
  "s2b.retake.title": "Take a new selfie",
  "s2b.retake.body": "A new photo is a new capture, so we'll ask for your consent again before generating anything.",
  "s2b.legal.link": "How we handle your photo",
  "s2b.legal.body":
    "Either way we draw your avatar from a photo of your face, so we confirm your age and your consent before generating. Your photo stays on file for reuse and is only ever used to draw your avatars.",

  /* ── S3 · Email, consent, code (/create/verify) ── */
  "s3.header.step": "Step 3 of 4",
  "s3.header.back": "Back",
  "s3.errors.missingPrereq": "Missing selfie or session. Go back and re-upload.",
  "s3.collect.title": "Where should your card be sent?",
  "s3.collect.lede": "We'll email you a code to check it's really you and use the same address for order updates.",
  "s3.collect.emailLabel": "Email address",
  "s3.collect.emailPlaceholder": "you@example.com",
  "s3.collect.emailHelp": "One card per email address. You'll need access to this inbox to finish your order.",
  "s3.consent.biometricTitle": "Yes, measure my face to make my avatar",
  "s3.consent.biometricBody":
    "I consent to my selfie being processed to generate an avatar (biometric processing, policy v1).",
  "s3.consent.termsTitle": "I accept the terms of use and the biometric policy",
  "s3.consent.termsBody": "I accept the Terms of Service and Privacy Policy (v1).",
  "s3.email.continue": "Continue",
  "s3.consent.stepTitle": "Two things to confirm",
  "s3.consent.stepLede": "Each one is recorded separately.",
  "s3.collect.stickyNote": "We send a {otpLength}-digit code. You confirm consent before we generate anything.",
  "s3.collect.sendCode": "Send code",
  "s3.collect.sending": "Sending…",
  "s3.code.title": "Check your inbox.",
  "s3.code.lede": "We sent a {otpLengthLabel} code to {email}.",
  "s3.code.changeEmail": "Wrong address? Change it",
  "s3.code.otpPlaceholder": "code",
  "s3.code.resendPrompt": "Didn't arrive?",
  "s3.code.resend": "Resend code",
  "s3.code.recording": "Recording consent…",
  "s3.code.graduating": "Creating your account…",
  "s3.code.submit": "Verify and continue",
  "s3.code.verifying": "Verifying…",
  "s3.errors.sendFailed": "Could not send code",
  "s3.errors.verifyFailed": "Verification failed",

  /* ── S4 · Generating (/create/generating) ── */
  "s4.header.stepLabel": "Making your card",
  "s4.stage.checkingSelfie": "Checking your selfie",
  "s4.stage.drawingFace": "Drawing your face",
  "s4.stage.puttingInScene": "Putting you in the scene",
  "s4.stage.layingOutCard": "Laying out your card",
  "s4.status.preparing": "Preparing…",
  "s4.status.bindingConsent": "Binding consent to selfie…",
  "s4.status.requesting": "Requesting generation…",
  "s4.status.generating": "Generating…",
  "s4.countdown.readyLabel": "ready",
  "s4.countdown.stillWorking": "still working",
  "s4.countdown.secondLeft": "second left",
  "s4.countdown.secondsLeft": "seconds left",
  "s4.heading.done": "There you are.",
  "s4.heading.slow": "Taking a bit longer.",
  "s4.heading.working": "Drawing you now.",
  "s4.body.done": "Your card is drawn and waiting. Have a look before you decide anything.",
  "s4.body.slow":
    "Nothing's wrong — busy queue. Leave this page whenever you like and we'll email you the moment it lands.",
  "s4.cta.seeCard": "See my card",
  "s4.footer.privacyNote":
    "Your photo is used to draw your avatar, stays on file so you can reuse it, and is never used for anything else.",
  "s4.errors.notSignedIn": "Not signed in — restart the flow.",
  "s4.errors.missingSelfieOrStyle": "Missing selfie or style pick.",
  "s4.errors.noGenerationsQueued": "No generations were queued.",

  /* ── S5 · Reveal, colour, orientation, size (/create/reveal, /create/share) ── */
  "s5.header.step": "Step 4 of 4",
  "s5.header.back": "Back to styles",
  "s5.header.title": "There you are.",
  "s5.header.subline": "{styleName}, on {cname}. Make it yours below, or order it as it is.",
  "s5.versions.label": "Your versions",
  "s5.versions.variantLabel": "v",
  "s5.versions.newLabel": "New",
  "s5.versions.regeneratingLabel": "…",
  "s5.customize.background": "Background",
  "s5.customize.orientation": "Orientation",
  "s5.customize.orientationPortrait": "Portrait",
  "s5.customize.orientationLandscape": "Landscape",
  "s5.customize.avatarSize": "Avatar size",
  "s5.customize.dragHint": "Drag your avatar to reposition it on the card.",
  "s5.actions.anotherStyle": "Another style",
  "s5.actions.regenerate": "Regenerate avatars",
  "s5.actions.regenerating": "Generating…",
  "s5.actions.regenerateLimit": "No designs left",
  "s5.actions.retake": "Retake selfie",
  "s5.actions.retaking": "Uploading new selfie…",
  "s5.share.title": "Show it off",
  "s5.share.lede": "We'll build the image for you — card and backdrop, sized right for wherever it's going.",
  "s5.share.formatStory": "Story 9:16",
  "s5.share.formatPost": "Post 1:1",
  "s5.share.brandLine": "Your Face. Your Card.",
  "s5.share.captionLabel": "Caption",
  // No code, no discount: neither exists yet. Do not reintroduce either here.
  "s5.share.defaultCaption": "Check out my cardmoji debit card design. Make your own at Cardmoji",
  "s5.share.charactersSuffix": "characters",
  "s5.share.copyCaption": "Copy caption",
  "s5.share.copied": "Copied",
  "s5.share.tileStories": "Stories",
  "s5.share.tileTikTok": "TikTok",
  "s5.share.tileWhatsApp": "WhatsApp",
  "s5.share.tilePost": "Post",
  "s5.share.tileMessages": "Messages",
  "s5.share.tileSave": "Save image",
  "s5.share.savedNote": "Saved",
  "s5.share.sharedNote": "Shared to {channel}",
  "s5.share.linkCopiedNote": "Link copied",
  // Each step's CTA names the NEXT decision, so the sequence is self-describing.
  "s5.actions.next": "Continue",
  "s5.actions.nextColour": "Change color",
  "s5.actions.nextLayout": "Change layout",
  "s5.actions.nextSize": "Change size",
  "s5.actions.addName": "Add your name",
  "s5.actions.continue": "Order my card",
  "s5.actions.later": "Save it and decide later",
  "s5.errors.noGenerations": "No generations available yet — please retake.",
  "s5.errors.uploadUrlMissing": "upload_url_missing",
  "s5.errors.retakeDeleteFailed": "retake_delete_failed: {message}",
  "s5.errors.missingSelfieOrStyle": "Missing selfie or style — please retake.",
  "s5.errors.regenerationFailed": "regeneration_failed",
  "s5.errors.generationSlow": "Generation is taking longer than expected — try again shortly.",

  /* ── S6 · Details steps + load (/create/name … /create/load) ── */
  "s6.header.backLabel": "Back to your card",
  "s6.header.stepRepeat": "Step 3 of 4",
  "s6.header.stepFirst": "Step 5 of 6",
  "s6.title.repeat": "Still you? Just check.",
  "s6.title.first": "The bank bit. Once, then never again.",
  "s6.lede.repeat":
    "We already have your details from last time. Have a quick look, change anything that's moved, and carry on.",
  /* COUNSEL-OWNED VOCABULARY: "a participating bank" — the same category the
     L11 handoff disclosure names. Never "our issuing bank" or "our banking
     partner": those assert a specific relationship and contradict the generic
     form counsel's §11b position rests on. */
  "s6.lede.first":
    "Your card is a real prepaid card, so a participating bank has to know who's holding it. This is the only form in the whole thing.",
  "s6.legal.notice":
    "Required by law for any card account — we pass it to a participating bank for identity checks and use it to post your card out. No credit check, and it never touches your avatar.",
  "s6.legal.link": "What the bank does with it",
  "s6.onfile.title": "Your details on file",
  "s6.onfile.nameLabel": "Name",
  "s6.onfile.deliveryLabel": "Delivery",
  "s6.onfile.deliveryFallback": "Details needed",
  "s6.onfile.note": "Your identity checks from your first card still stand — nothing to redo.",
  "s6.onfile.confirm": "These are right — continue",
  "s6.onfile.edit": "Something's changed — edit",
  "s6.name.title": "Your name",
  "s6.name.hint": "As it appears on your ID — this is what gets printed on the card.",
  "s6.name.first": "First name",
  "s6.name.firstPlaceholder": "My first name",
  "s6.name.middle": "Middle name",
  "s6.name.optionalBadge": "Optional",
  "s6.name.middlePlaceholder": "Leave blank if you'd rather",
  "s6.name.last": "Last name",
  "s6.name.lastPlaceholder": "My last name",
  "s6.name.printsAs": "Prints as",
  "s6.contact.title": "Phone & date of birth",
  "s6.contact.phoneLabel": "Cell number",
  "s6.contact.phoneHint": "Used for card security alerts, not marketing.",
  "s6.contact.phonePlaceholder": "(555) 019 2847",
  "s6.contact.dobLabel": "Date of birth",
  /* {minAge} resolves from the PROGRAMME age gate — see s1b.refusal.line1. */
  "s6.contact.dobHint": "You must be {minAge} or over. We check this, we don't guess it.",
  "s6.contact.dobMonthPlaceholder": "MM",
  "s6.contact.dobDayPlaceholder": "DD",
  "s6.contact.dobYearPlaceholder": "YYYY",
  "s6.delivery.title": "Delivery",
  "s6.delivery.inBranch": "Collect in branch",
  "s6.delivery.mail": "Post to me",
  "s6.delivery.branchLabel": "Branch",
  "s6.delivery.branchPlaceholder": "Branch name or ID",
  "s6.address.title": "Where should we post it?",
  "s6.address.hint": "Enter your address.",
  "s6.address.zipLabel": "ZIP code",
  "s6.address.zipError": "A ZIP code is 5 digits.",
  "s6.address.zipPlaceholder": "10001",
  "s6.address.change": "Change",
  "s6.address.street": "Street address",
  "s6.address.streetPlaceholder": "214 W 29th St",
  "s6.address.unit": "Apartment, floor, unit",
  "s6.address.unitPlaceholder": "Apt 4B",
  "s6.address.city": "City",
  "s6.address.state": "State",
  "s6.address.usOnly": "Cards go to US addresses only. No PO boxes — the bank won't accept them.",
  "s6.pan.label": "Last digits of your card",
  "s6.load.title": "How much do you plan to load?",
  "s6.load.hint":
    "Tell us what you plan to put on the card and we pass it to your bank with your order. Cardmoji never takes this money — it's yours, and you move it through your bank.",
  "s6.load.continue": "Continue",
  "s6.load.note":
    "How much you plan to load. You'll fund and activate your card through your bank's app, and you can change the amount there.",
  "s6.fee.title": "What Cardmoji charges",
  "s6.fee.singleLabel": "This order",
  "s6.fee.note":
    "This is the only amount Cardmoji charges. You'll fund and activate your card separately through your bank's app.",
  "s6.color.title": "Card color",
  "s6.orientation.title": "Orientation",
  "s6.design.title": "Your card design",
  "s6.design.lockedNote":
    "This is the card that will be ordered. To change the color or how your avatar sits, go back to the design step.",
  "s6.design.editLink": "Back to design",
  "s6.onfile.cancel": "Cancel edit",
  "s6.footer.note": "Nothing is submitted until you tap continue.",
  "s6.footer.continue": "Continue to delivery",

  /* ── S7 · Review and payment (/create/pay, /create/authorise) ── */
  "s7.header.backLabel": "Back to your details",
  "s7.header.stepRepeat": "Step 4 of 4",
  "s7.header.stepFirst": "Step 6 of 6",
  "s7.title": "Nothing leaves your account yet.",
  /* U4 · the hold/decline explanation lives ONLY in the counsel-owned security
     notice under the card form (`s7.security.notice`). Stating it twice on the
     same screen would be two statements about money; the subtitle is empty. */
  "s7.lede": "",
  "s7.verified.emailSuffix": "— verified. Add styles freely, no code to re-enter.",
  "s7.verified.fallback": "Your account is verified.",
  "s7.verified.repeatNote": " We'll use the card you paid with last time — you can change it below.",
  "s7.order.title": "Your order",
  /* NEW COPY (order-flow change): the label above the card-count choice. It
     states no price of its own — the options print the CONFIGURED tier labels
     and prices, so this line can never disagree with configuration. */
  "s7.order.quantityLabel": "How many cards",
  "s7.order.feeLabel": "Cardmoji card fee",
  "s7.order.totalLabel": "Total due to Cardmoji",
  "s7.order.feeUnavailable": "Confirmed at checkout",
  "s7.order.loadLabel": "You plan to load",
  "s7.order.loadNote":
    "Not charged by Cardmoji and not in the total above. It's your money — you'll fund and activate the card through your bank's app.",
  "s7.delivery.collectPrefix": "Collect: ",
  "s7.delivery.collectFallback": "—",
  "s7.delivery.mailPrefix": "Mail to ",
  "s7.delivery.mailFallback": "—",
  /* L11 · HANDOFF DISCLOSURE — quoted from counsel's generic form, used because
     the recipient bank is NOT known at submission (routing resolves server-side
     after consent). It names the CATEGORY; the instance is named at allocation
     by the `allocation_notified` email. Vocabulary is "participating bank" to
     match the three register documents. DO NOT paraphrase, shorten or split. */
  "s7.legal.handoff":
    "By continuing, you ask Cardmoji to send this application to a participating bank so it can decide whether to issue your card. The bank will process your information under its own privacy notice. Cardmoji does not make the approval, identity-verification or eligibility decision.",
  "s7.security.notice":
    "Card details are handled by our payment processor and never reach Cardmoji's servers. Nothing is charged until the bank approves your card. If they decline, the hold drops off and you pay nothing.",
  "s7.footer.noteBank": "No charge yet — your card will be prepared by the bank.",
  "s7.footer.noteWaitlist": "Card capture (Stripe SetupIntent) will run in the next release.",
  "s7.footer.submitting": "Authorizing…",
  "s7.footer.submitBank": "Authorize",
  "s7.footer.reviewContinue": "Continue to payment",
  "s7.footer.authoriseHint": "Authorize the card fee above to place your order.",
  "s7.footer.preAuthorize": "Pre-authorize {amount}",
  /* B7 · DISCOUNTED CHECKOUT. The amount authorized is resolved SERVER-SIDE
     (referral_discount_quote), so the screen must state the same number rather
     than the list fee — otherwise the pinned button promises one figure and the
     hold takes another. Figures are substituted from the server quote; none is
     typed here. COUNSEL: this is money wording on a live screen and needs
     review with the referral terms. */
  "s7.price.feeLine": "Card fee {fee}",
  "s7.price.discountLine": "Referral discount −{discount}",
  "s7.price.dueLine": "You authorize {due}",

  "s7.footer.placingOrder": "Payment authorized — placing your order…",
  "s7.done.title": "Order in.",
  "s7.done.body": "Taking you to your confirmation",
  "s7.error.notSignedIn": "Not signed in.",
  "s7.error.confirmTimeout": "Payment confirmation took too long. Please try again.",

  /* ── S8 · Confirmed (/create/confirmed) ── */
  "s8.status.pending": "Awaiting bank approval",
  "s8.status.waitlisted": "On the waitlist",
  "s8.status.approved": "Approved",
  "s8.status.rejected": "Not approved",
  "s8.status.fallback": "Awaiting bank approval",
  "s8.header.accountLink": "Your account",
  "s8.title": "Order in. Bank's turn.",
  "s8.lede": "We'll email you once the bank approves.",
  "s8.ref.copied": "Copied",
  "s8.ref.orderPrefix": "Order ",
  "s8.order.title": "In this order",
  "s8.order.firstCard": "First card",
  "s8.order.notCharged":
    "Not yet charged. Each card is approved separately — if one is declined we refund that card and send the rest.",
  "s8.timeline.title": "What happens next",
  "s8.timeline.receivedTitle": "Order received",
  "s8.timeline.receivedBody": "",
  "s8.timeline.receivedEmailSuffix": "Email sent to ",
  /* No timing claim: nothing here says a bank is reviewing right now. */
  "s8.timeline.decisionTitle": "Bank decision",
  "s8.timeline.decisionBody": "",
  "s8.timeline.productionTitle": "Card production",
  "s8.timeline.productionBody": "",
  "s8.timeline.postTitle": "In the post",
  "s8.timeline.postBodyPostedPrefix": "Posted to ",
  /* {limit} comes from PROGRAM SETTINGS (public_config.maxLoadCents). It is a
     regulatory figure that varies by program, so it is never hardcoded and the
     line is omitted entirely when the program carries no value. */
  "s8.timeline.postBody": "Activate your card and load up to {limit} when it arrives",
  /* RETIRED 2026-09-09: the four s8.waitBanner.* keys reached no customer.
     Referral wording that IS shown lives on S9/S10 and resolves its figures
     from program_referral_settings. Do not re-add these without a specified
     screen to place them on. */

  "s8.actions.account": "Go to my account",
  "s8.actions.addAnother": "Add another card",
  "s8.support.question": "Questions about this order?",
  "s8.support.link": "Contact support",
  "s8.support.withOrder": " with order {ref}.",
  "s8.support.period": ".",
  "s8.loading": "Loading your order…",

  /* ── S9 · Account (/account) ── */
  "s9.header.accountMenuLabel": "Account menu",
  "s9.menu.yourCards": "Your cards",
  "s9.menu.sharingEarnings": "Sharing & discounts",
  "s9.menu.addAnotherCard": "Add another card",
  "s9.menu.logout": "Log out",
  "s9.header.title": "My account",
  "s9.body.loading": "Loading your account…",
  "s9.body.signedOut": "You're not signed in.",
  "s9.body.signInLink": "Sign in",
  "s9.body.startCreating": "Make a card",
  "s9.body.signedOutSuffix": "to see your cards, or start a new one.",
  "s9.profile.sectionLabel": "Profile",
  "s9.profile.nameLabel": "Name on cards",
  "s9.profile.emailLabel": "Email",
  "s9.profile.addressLabel": "Delivery address",
  "s9.cards.sectionTitle": "Cards",
  "s9.cards.addAnother": "Add another →",
  "s9.cards.empty": "No cards yet.",
  "s9.cards.startFirst": "Start your first design",
  "s9.cards.status.pending": "Awaiting bank approval",
  "s9.cards.status.waitlisted": "On the waitlist",
  "s9.cards.status.approved": "Approved",
  "s9.cards.status.rejected": "Not approved",
  "s9.cards.subtitle.approved": "Approved {date}",
  "s9.cards.subtitle.approvedNoDate": "Approved",
  "s9.cards.subtitle.rejected": "Not approved by the bank · {date}",
  "s9.cards.subtitle.rejectedNoDate": "Not approved by the bank",
  "s9.cards.subtitle.waitlisted": "On the waitlist",
  "s9.cards.subtitle.submitted": "Submitted {date} · with the bank",
  "s9.cards.subtitle.submittedNoDate": "With the bank",
  "s9.info.bankDisclosure":
    "Card numbers, balances and transactions live with a participating bank, not with us — use their app or statement for those. We show you the design, the order and the decision.",
  "s9.sharing.sectionTitle": "Sharing & discounts",
  "s9.tabs.share": "Share",
  "s9.tabs.cards": "Cards",
  "s9.tabs.profile": "Profile",
  "s9.sharing.yourCode": "Your code",
  "s9.sharing.copyCode": "Copy code",
  "s9.sharing.copied": "Copied",
  "s9.sharing.shareCard": "Share a card",
  /* L15 · COUNSEL-OWNED MONEY CLAIM. Figures are TOKENS resolved from
     program_referral_settings at render (src/lib/referral-terms.ts) — never
     typed. A referral reward is a discount off the Cardmoji card fee: no cash,
     no payout, no minimum, one discount per card, and they do not stack. The
     sentence is omitted entirely when config grants nothing. */
  "s9.sharing.terms":
    "Friends get {{inviteeReward}} off the Cardmoji card fee on their first card. When their card ships you get {{referrerReward}} off the fee on a card of your own — one discount per card, applied at checkout. There is no cash payout.",
  "s9.sharing.referralsLabel": "Referrals",
  "s9.sharing.earnedLabel": "Discounts earned",
  "s9.footer.disclaimer":
    "Cardmoji is not a bank and not a card issuer. Cards are issued by a participating bank, which makes all approval decisions.",

  /* L11 · COUNSEL-OWNED. Who approves and who issues, on the standing screen.
     Same category as the "your bank" lines: do not reword without counsel.
     No issuance date and no implied timeline may be added here. */
  "s9b.honesty":
    "This is where you stand today. Sharing your code is the only thing that moves you up. No issuance date is set, and we won't imply one — approval and issuing are the decision of a participating bank, not ours.",



  /* ── S10 · Creator (/creator) ── */
  "s10.header.badge": "Creator",
  "s10.header.accountMenuLabel": "Account menu",
  "s10.menu.switchToCards": "Switch to my cards",
  "s10.menu.logout": "Log out",
  "s10.header.title": "Your creator hub",
  "s10.header.subtitle":
    "Your code and the discounts it has earned you. Everything here reflects your real referral activity.",
  "s10.body.loading": "Loading your creator hub…",
  "s10.body.signedOut": "You're not signed in.",
  "s10.body.signIn": "Sign in",
  "s10.body.signedOutSuffix": "to see your creator hub.",
  "s10.code.sectionLabel": "Your code",
  "s10.code.copyCode": "Copy code",
  "s10.code.copiedCode": "Copied",
  "s10.code.copyLink": "Copy link",
  "s10.code.copiedLink": "Link copied",
  /* L15 · COUNSEL-OWNED MONEY CLAIM. Figures are TOKENS resolved from
     program_referral_settings at render (src/lib/referral-terms.ts). The reward
     is a discount off the Cardmoji card fee — never cash, no payout, no
     minimum. Omitted entirely when config grants nothing. */
  "s10.code.terms":
    "Your audience gets {{inviteeReward}} off the Cardmoji card fee on their first card. When their card ships you get {{referrerReward}} off the fee on a card of your own — one discount per card, applied at checkout. There is no cash payout.",
  "s10.code.noCode": "You don't have a referral code yet. Join the waitlist to get one.",
  "s10.reels.sectionTitle": "Approved reels",
  "s10.reels.empty": "Approved reels will show up here once we produce them for your code — nothing to post yet.",
  "s10.info.claimsDisclosure":
    "Say the card is a prepaid card, and that approval is the bank's decision. Don't promise anyone they'll be approved, and don't describe it as a bank account — those two claims are what get posts pulled.",
  "s10.performance.sectionTitle": "Performance",
  "s10.performance.cardsApproved": "Cards approved via your code",
  "s10.performance.earnedLabel": "Discounts earned",

  "s10.footer.disclaimer":
    "Cardmoji is not a bank and not a card issuer. Cards are issued by a participating bank, which makes all approval decisions.",

  /* ── keys with placeholders in their editor label ── */
  "s1b.affirm.label": "I confirm I am {{threshold}} or over.",
  "s1b.affirm.decline": "I'm under {{threshold}}",
  "s4.attempts": "{used} of {total} designs used",
  "s5.versions.attempts": "{used} of {total} designs used",
  "s6.pan.hint": "Enter the last {digits} digits.",
};
